import { t } from '@lingui/macro'
import { Button } from '@mui/material'
import type { ContactAttemptOutcome } from '@om1/aspen-api/codegen/client/models/ContactAttemptOutcome'
import type { ContactAttemptType } from '@om1/aspen-api/codegen/client/models/ContactAttemptType'
import type { PatientManagerIndexDTO } from '@om1/aspen-api/codegen/client/models/PatientManagerIndexDTO'
import { PatientManagersService } from '@om1/aspen-api/codegen/client/services/PatientManagersService'
import { SubjectType, useSubjectAuditHistory } from '@om1/aspen-api/codegen/hooks/useSubjectAuditHistory'
import { formatDate, formatDateTimeET } from '@om1/platform-components'
import { Pagination } from 'antd'
import { Clock, RefreshCw } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getAttemptOutcomeDisplay, getContactTypeDisplay } from './LogAttemptInput'

interface SubjectHistoryProps {
    subjectType: SubjectType
    subjectId: string
    onUpdate?: () => void
    patientManagers?: PatientManagerIndexDTO[]
}

// Format field names for display (convert camelCase or snake_case to Title Case)
const formatFieldName = (fieldName: string): string => {
    // Normalize: remove underscores, convert to lowercase for pattern matching
    const normalized = fieldName.toLowerCase().replace(/_/g, '')

    // Split field name into words
    let words: string[] = []

    if (fieldName.includes('_')) {
        // Handle snake_case
        words = fieldName.split('_').filter(Boolean)
    } else {
        // Handle camelCase - split on capital letters
        words = fieldName
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .split(/\s+/)
            .filter(Boolean)
    }

    // Format words with intelligent handling
    const formattedWords = words.map((word, index) => {
        const lowerWord = word.toLowerCase()

        // Handle common patterns
        if (lowerWord === 'id' && index === words.length - 1) {
            // "Id" at the end (e.g., "patient_id" -> "Patient Id")
            return t`Id`
        }
        if (lowerWord === 'manager' && index > 0) {
            // "Manager" when not first word (e.g., "patient_manager" -> "Patient Manager")
            return t`Manager`
        }
        if (lowerWord === 'sms') {
            return 'SMS'
        }
        if (lowerWord === 'dob' || (lowerWord === 'birth' && words[index - 1]?.toLowerCase() === 'of')) {
            // Handle "date of birth" pattern
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        }

        // Default: capitalize first letter, lowercase rest
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })

    // Join words and handle special patterns
    let result = formattedWords.join(' ')

    // Post-process common patterns
    // "Patient Manager Id" -> "Patient Manager" (remove "Id" for manager fields)
    if (normalized.includes('patientmanager') && result.endsWith(' Id')) {
        result = result.replace(/\s+Id$/, '')
    }
    // "Date Of Birth" -> "Date of Birth" (lowercase "of")
    result = result.replace(/\bOf\b/g, 'of')
    // "Attempt Outcome" -> "Contact Outcome" (semantic mapping)
    if (normalized.includes('attemptoutcome')) {
        result = t`Contact Outcome`
    }
    // "Suggested Next Contact Time" -> "Next Contact" (simplify)
    if (normalized.includes('suggestednextcontacttime')) {
        result = t`Next Contact`
    }

    return result
}

// Format field value for display
const formatFieldValue = (value: any, fieldName?: string): string => {
    if (value === null || value === undefined) {
        return t`N/A`
    }

    // Handle contact type and outcome with display functions
    if (fieldName === 'contactType' || fieldName === 'contact_type') {
        try {
            const display = getContactTypeDisplay(value as ContactAttemptType)
            if (display) {
                return String(display)
            }
        } catch {
            // Fall through to return String(value)
        }
    }

    if (fieldName === 'attemptOutcome' || fieldName === 'attempt_outcome') {
        try {
            const display = getAttemptOutcomeDisplay(value as ContactAttemptOutcome)
            if (display) {
                return String(display)
            }
        } catch {
            // Fall through to return String(value)
        }
    }

    // Handle status values - convert underscores to spaces and title case
    if (fieldName === 'status' && typeof value === 'string') {
        return value
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
    }

    // Handle boolean values
    if (typeof value === 'boolean') {
        return value ? t`Yes` : t`No`
    }

    // Next contact time: show full date+time in ET (backend stores UTC)
    const isNextContactField =
        fieldName &&
        (fieldName.toLowerCase().includes('suggestednextcontacttime') ||
            fieldName.toLowerCase().includes('suggested_next_contact_time') ||
            fieldName.toLowerCase().includes('next contact'))
    if (isNextContactField && typeof value === 'string' && value.includes('T')) {
        try {
            const date = new Date(value)
            if (!isNaN(date.getTime())) {
                return formatDateTimeET(value)
            }
        } catch {
            // Fall through
        }
    }

    // Handle date strings (date-only, no time)
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/) && !value.includes('T')) {
        try {
            return formatDate(value)
        } catch {
            return value
        }
    }

    // Handle other datetime strings (e.g. createdDttm in audit) – show in ET for consistency
    if (typeof value === 'string' && value.includes('T')) {
        try {
            const date = new Date(value)
            if (!isNaN(date.getTime())) {
                return formatDateTimeET(value)
            }
        } catch {
            // Fall through to return as string
        }
    }

    return String(value)
}

// Format operation type for display
const formatOperation = (operation: string): string => {
    const operations: Record<string, string> = {
        C: t`Created`,
        U: t`Updated`,
        D: t`Deleted`,
        T: t`Truncated`
    }
    return operations[operation] || operation
}

// Format audit log entry for display
interface FormattedAuditEntry {
    fieldName: string
    oldValue: string
    newValue: string
    userName: string
    date: string
    isContactAttempt: boolean
    isCarePartner: boolean
    isCioxRequest: boolean
    operation: string
}

const formatAuditEntry = (log: any): FormattedAuditEntry[] => {
    const { operation, changedFields, originalValues, newValues, createdByEmail, createdDttm, entityType } = log
    const userName = createdByEmail || t`System`
    const isContactAttempt =
        entityType === 'candidate_contact_attempt' || entityType === 'patient_contact_attempt' || entityType === 'care_partner_contact_attempt'
    const date = isContactAttempt ? formatDateTimeET(createdDttm) : formatDate(createdDttm)
    const isCarePartner = entityType === 'care_partner'
    const isCioxRequest = entityType === 'ciox_request'

    // Handle CREATE operation
    if (operation === 'C') {
        if (!newValues || Object.keys(newValues).length === 0) {
            return [
                {
                    fieldName: t`Entity`,
                    oldValue: '',
                    newValue: t`Created`,
                    userName,
                    date,
                    isContactAttempt: false,
                    isCarePartner,
                    isCioxRequest,
                    operation: 'C'
                }
            ]
        }
        // For CREATE, show initial fields
        return Object.keys(newValues).map((field) => ({
            fieldName: formatFieldName(field),
            oldValue: '',
            newValue: formatFieldValue(newValues[field], field),
            userName,
            date,
            isContactAttempt,
            isCarePartner,
            isCioxRequest,
            operation: 'C'
        }))
    }

    // Handle DELETE operation
    if (operation === 'D') {
        return [
            {
                fieldName: t`Entity`,
                oldValue: '',
                newValue: t`Deleted`,
                userName,
                date,
                isContactAttempt: false,
                isCarePartner,
                isCioxRequest,
                operation: 'D'
            }
        ]
    }

    // Handle UPDATE operation
    if (operation === 'U' && changedFields) {
        const entries: FormattedAuditEntry[] = []

        for (const field in changedFields) {
            const oldValue = originalValues?.[field]
            const newValue = newValues?.[field]

            entries.push({
                fieldName: formatFieldName(field),
                oldValue: formatFieldValue(oldValue, field),
                newValue: formatFieldValue(newValue, field),
                userName,
                date,
                isContactAttempt,
                isCarePartner,
                isCioxRequest,
                operation: 'U'
            })
        }

        return entries
    }

    return [
        {
            fieldName: t`Entity`,
            oldValue: '',
            newValue: formatOperation(operation),
            userName,
            date,
            isContactAttempt: false,
            isCarePartner,
            isCioxRequest,
            operation
        }
    ]
}

export const SubjectHistory: React.FC<SubjectHistoryProps> = ({ subjectType, subjectId, onUpdate, patientManagers = [] }) => {
    const { auditLogs, meta, isLoading, error, refetch, setPage, setLimit } = useSubjectAuditHistory({
        subjectType,
        subjectId,
        page: 1,
        limit: 20,
        sort: 'created_dttm:desc'
    })

    // Create a map of patient manager IDs to names from provided patient managers
    const patientManagerMap = useMemo(() => {
        const map: Record<string, string> = {}
        patientManagers.forEach((manager) => {
            if (manager.id && manager.name) {
                map[manager.id] = manager.name
            }
        })
        return map
    }, [patientManagers])

    // Extract unique patient manager UUIDs from audit logs
    const managerIds = useMemo(() => {
        const ids = new Set<string>()
        auditLogs.forEach((log) => {
            const { changedFields, originalValues, newValues, operation } = log

            // Check changedFields for patient_manager_id in UPDATE operations
            if (operation === 'U' && changedFields) {
                for (const field in changedFields) {
                    if (field === 'patient_manager_id' || field === 'patientManagerId') {
                        const oldValue = originalValues?.[field]
                        const newValue = newValues?.[field]

                        // Extract UUIDs (check if it's a valid UUID string format - 36 characters)
                        if (oldValue && typeof oldValue === 'string' && oldValue.length === 36) {
                            ids.add(oldValue)
                        }
                        if (newValue && typeof newValue === 'string' && newValue.length === 36) {
                            ids.add(newValue)
                        }
                    }
                }
            }

            // Check newValues for CREATE operations
            if (operation === 'C' && newValues) {
                for (const field in newValues) {
                    if (field === 'patient_manager_id' || field === 'patientManagerId') {
                        const value = newValues[field]
                        if (value && typeof value === 'string' && value.length === 36) {
                            ids.add(value)
                        }
                    }
                }
            }
        })
        return Array.from(ids)
    }, [auditLogs])

    // Fetch patient manager names only for IDs not in provided patient managers
    const [patientManagerNames, setPatientManagerNames] = useState<Record<string, string>>({})
    const fetchedIdsRef = useRef<Set<string>>(new Set())

    useEffect(() => {
        const fetchMissingPatientManagerNames = async () => {
            if (managerIds.length === 0) {
                return
            }

            // Find IDs that are not in the provided patient managers and haven't been fetched
            const idsToFetch = managerIds.filter((id) => !patientManagerMap[id] && !fetchedIdsRef.current.has(id))

            if (idsToFetch.length === 0) {
                return
            }

            // Mark these IDs as being fetched
            idsToFetch.forEach((id) => fetchedIdsRef.current.add(id))

            try {
                const fetchedNames: Record<string, string> = {}
                // Fetch only the missing patient managers in parallel
                const promises = idsToFetch.map(async (id) => {
                    try {
                        const manager = await PatientManagersService.getPatientManagerPatientManagersPatientManagerIdGet({
                            patientManagerId: id,
                            includeInactive: true // Include inactive for historical records
                        })
                        fetchedNames[id] = manager.name
                    } catch (err) {
                        console.warn(t`Failed to fetch patient manager with ID ${id}:`, err)
                        // Remove from ref if fetch fails so we can retry later
                        fetchedIdsRef.current.delete(id)
                    }
                })
                await Promise.all(promises)
                setPatientManagerNames((prev) => ({ ...prev, ...fetchedNames }))
            } catch (err) {
                console.error(t`Error fetching patient manager names:`, err)
                // Remove from ref on error so we can retry later
                idsToFetch.forEach((id) => fetchedIdsRef.current.delete(id))
            }
        }

        fetchMissingPatientManagerNames()
    }, [managerIds, patientManagerMap])

    // Reset patient manager cache when subjectId changes (but don't refetch - the hook handles that)
    React.useEffect(() => {
        fetchedIdsRef.current.clear()
        setPatientManagerNames({})
        // Note: We don't call refetch() here because useSubjectAuditHistory already handles fetching when subjectId changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subjectId])

    if (error) {
        return (
            <div className='bg-white rounded-lg border border-gray-200 overflow-hidden mb-4 shadow-sm'>
                <div className='flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50'>
                    <div className='flex items-center gap-2'>
                        <div className='text-primary-main'>
                            <Clock size={18} />
                        </div>
                        <h3 className='text-base font-semibold text-gray-800 m-0'>{t`Subject History`}</h3>
                    </div>
                </div>
                <div className='p-4'>
                    <p className='text-sm text-red-500'>{error}</p>
                </div>
            </div>
        )
    }

    return (
        <div className='bg-white rounded-lg border border-gray-200 overflow-hidden mb-4 shadow-sm'>
            <div className='flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50'>
                <div className='flex items-center gap-2'>
                    <div className='text-primary-main'>
                        <Clock size={18} />
                    </div>
                    <h3 className='text-base font-semibold text-gray-800 m-0'>{t`Subject History`}</h3>
                </div>
                <Button
                    onClick={() => refetch()}
                    disabled={isLoading}
                    size='small'
                    variant='outlined'
                    className='h-8'
                    startIcon={<RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />}
                >
                    {t`Refresh`}
                </Button>
            </div>
            <div className='p-4'>
                {isLoading ? (
                    <div className='text-center py-8'>
                        <p className='text-sm text-gray-500'>{t`Loading history...`}</p>
                    </div>
                ) : auditLogs.length === 0 ? (
                    <div className='text-center py-8'>
                        <p className='text-sm text-gray-500'>{t`No history available`}</p>
                    </div>
                ) : (
                    <>
                        <div className='max-h-[300px] overflow-y-auto pr-2'>
                            <ul className='space-y-3 list-none pl-0'>
                                {auditLogs.map((log) => {
                                    const entries = formatAuditEntry(log)

                                    // Group contact attempt entries together
                                    if (entries.length > 0 && entries[0].isContactAttempt && entries.length > 1) {
                                        // Extract key fields for better formatting
                                        // Match field names (could be from formatFieldName or raw field names)
                                        const contactTypeEntry = entries.find((e) => {
                                            const name = e.fieldName.toLowerCase()
                                            return name.includes('contact type') || name === 'contacttype' || name === 'contact_type'
                                        })
                                        const outcomeEntry = entries.find((e) => {
                                            const name = e.fieldName.toLowerCase()
                                            return (
                                                name.includes('outcome') ||
                                                name.includes('contact outcome') ||
                                                name === 'attemptoutcome' ||
                                                name === 'attempt_outcome'
                                            )
                                        })
                                        const notesEntry = entries.find((e) => {
                                            const name = e.fieldName.toLowerCase()
                                            return name === 'notes' && e.newValue !== t`N/A`
                                        })
                                        const nextContactEntry = entries.find((e) => {
                                            const name = e.fieldName.toLowerCase()
                                            return (name.includes('next contact') || name.includes('suggested')) && e.newValue !== t`N/A`
                                        })

                                        // Build a cleaner display format
                                        const parts: string[] = []

                                        // Main line: Contact Type - Outcome
                                        // Use newValue for CREATE, show change for UPDATE
                                        const getDisplayValue = (entry: FormattedAuditEntry) => {
                                            if (entry.oldValue && entry.oldValue !== '') {
                                                return `${entry.oldValue} → ${entry.newValue}`
                                            }
                                            return entry.newValue
                                        }

                                        if (contactTypeEntry && outcomeEntry) {
                                            const contactDisplay = getDisplayValue(contactTypeEntry)
                                            const outcomeDisplay = getDisplayValue(outcomeEntry)
                                            parts.push(`${contactDisplay} - ${outcomeDisplay}`)
                                        } else if (contactTypeEntry) {
                                            parts.push(getDisplayValue(contactTypeEntry))
                                        } else if (outcomeEntry) {
                                            parts.push(getDisplayValue(outcomeEntry))
                                        }

                                        // Additional info on new lines (only if not N/A and has meaningful content)
                                        if (notesEntry && notesEntry.newValue !== t`N/A` && notesEntry.newValue.trim() !== '') {
                                            const notesDisplay = getDisplayValue(notesEntry)
                                            parts.push(`${t`Notes:`} ${notesDisplay}`)
                                        }
                                        if (nextContactEntry && nextContactEntry.newValue !== t`N/A` && nextContactEntry.newValue.trim() !== '') {
                                            const nextContactDisplay = getDisplayValue(nextContactEntry)
                                            parts.push(`${t`Next Contact:`} ${nextContactDisplay}`)
                                        }

                                        return (
                                            <li key={log.id} className='text-sm text-gray-700 border-b border-gray-100 pb-3 last:border-b-0'>
                                                <div className='space-y-1'>
                                                    <div>
                                                        <span className='font-medium'>{t`Contact Attempt:`} </span>
                                                        <span className='text-gray-900'>{parts[0] || t`Contact logged`}</span>
                                                    </div>
                                                    {parts.length > 1 && (
                                                        <div className='pl-4 text-gray-600 space-y-0.5'>
                                                            {parts.slice(1).map((part, idx) => (
                                                                <div key={idx}>{part}</div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className='text-xs text-gray-500 mt-1'>
                                                        {t`by ${entries[0].userName} on ${entries[0].date}`}
                                                    </div>
                                                </div>
                                            </li>
                                        )
                                    }

                                    // Render individual entries
                                    return entries.map((entry, entryIndex) => {
                                        // Add prefix for care partner and ciox request entries
                                        let prefix = ''
                                        if (entry.isCarePartner) {
                                            prefix = t`Care Partner:` + ' '
                                        } else if (entry.isCioxRequest) {
                                            prefix = t`Ciox Request:` + ' '
                                        }

                                        // Replace patient manager UUIDs with names in the display
                                        // Check if this is a patient manager field by comparing normalized field name
                                        const normalizedFieldName = entry.fieldName.toLowerCase().replace(/\s+/g, '')
                                        const isPatientManagerField =
                                            normalizedFieldName === 'patientmanager' ||
                                            normalizedFieldName === 'patientmanagerid' ||
                                            entry.fieldName === t`Patient Manager` ||
                                            entry.fieldName === 'Patient Manager Id' ||
                                            entry.fieldName === 'Patient Manager'

                                        let displayOldValue = entry.oldValue
                                        let displayNewValue = entry.newValue

                                        if (isPatientManagerField) {
                                            // Check provided patient managers first, then fetched names, then fallback to ID
                                            if (entry.oldValue && typeof entry.oldValue === 'string' && entry.oldValue.length === 36) {
                                                const foundName = patientManagerMap[entry.oldValue] || patientManagerNames[entry.oldValue]
                                                displayOldValue = foundName || entry.oldValue
                                                // Debug: log if name not found
                                                if (!foundName && entry.oldValue !== 'N/A') {
                                                    console.warn(t`Patient manager name not found for ID: ${entry.oldValue}`, {
                                                        inMap: !!patientManagerMap[entry.oldValue],
                                                        inFetched: !!patientManagerNames[entry.oldValue],
                                                        mapSize: Object.keys(patientManagerMap).length,
                                                        fetchedSize: Object.keys(patientManagerNames).length
                                                    })
                                                }
                                            }
                                            if (entry.newValue && typeof entry.newValue === 'string' && entry.newValue.length === 36) {
                                                const foundName = patientManagerMap[entry.newValue] || patientManagerNames[entry.newValue]
                                                displayNewValue = foundName || entry.newValue
                                                // Debug: log if name not found
                                                if (!foundName && entry.newValue !== 'N/A') {
                                                    console.warn(t`Patient manager name not found for ID: ${entry.newValue}`, {
                                                        inMap: !!patientManagerMap[entry.newValue],
                                                        inFetched: !!patientManagerNames[entry.newValue],
                                                        mapSize: Object.keys(patientManagerMap).length,
                                                        fetchedSize: Object.keys(patientManagerNames).length
                                                    })
                                                }
                                            }
                                        }

                                        return (
                                            <li
                                                key={`${log.id}-${entryIndex}`}
                                                className='text-sm text-gray-700 border-b border-gray-100 pb-3 last:border-b-0'
                                            >
                                                <div className='space-y-1'>
                                                    {entry.operation === 'C' && !entry.oldValue ? (
                                                        // CREATE operation
                                                        <div>
                                                            <span className='font-medium'>
                                                                {prefix}
                                                                {entry.fieldName}
                                                            </span>
                                                            <span> {t`set to`} </span>
                                                            <span className='text-green-600 font-medium'>{displayNewValue}</span>
                                                        </div>
                                                    ) : entry.operation === 'D' ? (
                                                        // DELETE operation
                                                        <div>
                                                            <span className='font-medium'>
                                                                {prefix}
                                                                {entry.fieldName}
                                                            </span>
                                                            <span> {displayNewValue}</span>
                                                        </div>
                                                    ) : (
                                                        // UPDATE operation
                                                        <div>
                                                            <span className='font-medium'>
                                                                {prefix}
                                                                {entry.fieldName}
                                                            </span>
                                                            <span> {t`changed from`} </span>
                                                            <span className='text-red-600 font-medium'>{displayOldValue}</span>
                                                            <span> → </span>
                                                            <span className='text-green-600 font-medium'>{displayNewValue}</span>
                                                        </div>
                                                    )}
                                                    <div className='text-xs text-gray-500'>{t`by ${entry.userName} on ${entry.date}`}</div>
                                                </div>
                                            </li>
                                        )
                                    })
                                })}
                            </ul>
                        </div>
                        {meta && meta.totalItems > 0 && (
                            <div className='mt-4 flex justify-end'>
                                <Pagination
                                    current={meta.currentPage}
                                    total={meta.totalItems}
                                    pageSize={meta.itemsPerPage}
                                    onChange={(page) => setPage(page)}
                                    onShowSizeChange={(current, size) => setLimit(size)}
                                    showSizeChanger
                                    pageSizeOptions={['10', '20', '50', '100']}
                                    showTotal={(total, range) => (
                                        <span>
                                            {range[0]}-{range[1]} {t`of`} {total} {t`items`}
                                        </span>
                                    )}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
